import * as THREE from "three";
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useStore } from "../../store/store";
import { useLocation, useRoute } from "wouter";
import HeaderButtons from "./HeaderButtons";
import ArtworkImageSection from "./ArtworkImageSection";
import ArtworkDetails from "./ArtworkDetails";
import { Artwork } from "../../types";
import { twMerge } from "tailwind-merge";
import audioManager from "../../audio";
import { useTransition, animated } from "@react-spring/web";

interface ArtworkDetailViewProps {
  uuid?: string; // If passed, we consider this as an inline mode
  onClose?: () => void; // Callback for when the artwork is closed
}

export interface ArtworkDetailViewRef {
  getArtwork: () => Artwork | null;
}

const ArtworkDetailView = forwardRef<
  ArtworkDetailViewRef,
  ArtworkDetailViewProps
>(({ uuid, onClose }, ref) => {
  const [isDirectLink, setIsDirectLink] = useState(false);
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const isInline = Boolean(uuid);

  // Store Actions
  const {
    setArtworkInFront,
    setSelectedArtwork,
    selectedArtwork,
    addArtworkToHistory,
  } = useStore();

  // Routing
  const [, navigate] = useLocation();
  const [, params] = useRoute("*/:uuid");

  // Refs
  const firstParagraphRef = useRef<HTMLDivElement>(null);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getArtwork: () => artwork,
  }));

  // Fetch Artwork Details
  useEffect(() => {
    const fetchArtworkDetails = async (uuid: string) => {
      try {
        const response = await fetch(
          `https://admin.collectieconnectie.nl/api/v1/artworks/${uuid}`,
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setArtwork(data.artwork);
        addArtworkToHistory(data.artwork);
      } catch (error) {
        console.error("Error fetching artwork details:", error);
      }
    };

    const _uuid = uuid || params?.uuid;
    if (_uuid && _uuid !== undefined) {
      if (!selectedArtwork) {
        setIsDirectLink(true);
      }
      setArtworkInFront(true);
      fetchArtworkDetails(_uuid);
    }
  }, [
    uuid,
    params?.uuid,
    selectedArtwork,
    setArtworkInFront,
    addArtworkToHistory,
  ]);

  // Handlers
  const onCloseArtworkClick = useCallback(() => {
    audioManager.play("click");
    if (isInline) {
      // If inline, just fade out
      setIsVisible(false);
    } else {
      // If not inline, navigate as before
      setArtworkInFront(false);
      setSelectedArtwork(null);
      navigate("/collection");
    }

    // Trigger the onClose callback if provided
    if (onClose) {
      onClose();
    }
  }, [isInline, setArtworkInFront, setSelectedArtwork, navigate, onClose]);

  const onScrollToClick = useCallback(() => {
    if (firstParagraphRef.current) {
      firstParagraphRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Handle Escape Key Press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseArtworkClick();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onCloseArtworkClick]);

  // CSS Classes
  const mainClasses = useMemo(
    () =>
      twMerge(
        "h-screen w-full absolute top-0 left-0 z-30 bg-transparent overflow-y-scroll overscroll-none",
        isDirectLink ? "backdrop--blur" : "",
      ),
    [isDirectLink],
  );

  // Transitions (only if isInline is true)
  const transitions = useTransition(isInline && isVisible, {
    from: { opacity: 0, backdropFilter: "blur(0px)", WebkitBackdropFilter: "blur(0px)" },
    enter: { opacity: 1, backdropFilter: "blur(30px)", WebkitBackdropFilter: "blur(30px)" },
    leave: { opacity: 0, backdropFilter: "blur(0px)", WebkitBackdropFilter: "blur(0px)" },
    config: { tension: 280, friction: 60 },
  });

  // Main Render Content
  const Content = () => (
    <>
      {/* Header Buttons */}
      {artwork && (
        <HeaderButtons
          isInline={isInline}
          onClose={onCloseArtworkClick}
          artwork={artwork}
        />
      )}

      {/* Artwork Image and More Info */}
      <ArtworkImageSection
        artwork={artwork}
        isDirectLink={isDirectLink}
        onScrollToClick={onScrollToClick}
      />

      {/* Artwork Details */}
      <ArtworkDetails artwork={artwork} firstParagraphRef={firstParagraphRef} />
    </>
  );

  return isInline ? (
    transitions((styles, item) =>
      item ? (
        <animated.div style={styles} className={mainClasses}>
          <Content />
        </animated.div>
      ) : null,
    )
  ) : (
    // Render without transition for non-inline mode
    <div className={mainClasses}>
      <Content />
    </div>
  );
});

export default ArtworkDetailView;
